/**
 * 项目模板定义
 * @module services/storage/templates
 */

import type { TemplateId, TemplateInfo } from '@/types';
import { MQTT_RUNTIME_PASSWORD_PLACEHOLDER } from '@/utils';

/** 空白模板 */
const BLANK_TEMPLATE: TemplateInfo = {
  id: 'blank',
  name: '空白项目',
  description: '从零开始创建您的项目',
  workspaceXml: '<xml xmlns="https://developers.google.com/blockly/xml"></xml>',
};

/** Hello World 模板 */
const HELLO_WORLD_TEMPLATE: TemplateInfo = {
  id: 'hello-world',
  name: 'Hello World',
  description: '一个简单的打印示例',
  image: '/img/yiblock/YC-AIS.png',
  workspaceXml: `<xml xmlns="https://developers.google.com/blockly/xml">
  <block type="text_print" x="50" y="50">
    <value name="TEXT">
      <shadow type="text">
        <field name="TEXT">Hello, YiBlock2!</field>
      </shadow>
    </value>
  </block>
</xml>`,
};

/** 条件判断示例模板 */
const LOGIC_BRANCH_TEMPLATE: TemplateInfo = {
  id: 'logic-branch',
  name: '条件判断',
  description: '使用判断积木控制分支逻辑',
  image: '/img/yiblock/YM-K3.png',
  workspaceXml: `<xml xmlns="https://developers.google.com/blockly/xml">
  <block type="controls_if" x="50" y="50">
    <value name="IF0">
      <block type="logic_compare">
        <field name="OP">GT</field>
        <value name="A">
          <shadow type="math_number">
            <field name="NUM">5</field>
          </shadow>
        </value>
        <value name="B">
          <shadow type="math_number">
            <field name="NUM">3</field>
          </shadow>
        </value>
      </block>
    </value>
    <statement name="DO0">
      <block type="text_print">
        <value name="TEXT">
          <shadow type="text">
            <field name="TEXT">条件成立</field>
          </shadow>
        </value>
      </block>
    </statement>
  </block>
</xml>`,
};

/** 循环示例模板 */
const LOOP_TEMPLATE: TemplateInfo = {
  id: 'loop-example',
  name: '循环示例',
  description: '展示如何使用循环积木',
  image: '/img/yiblock/YM-BEEP.png',
  workspaceXml: `<xml xmlns="https://developers.google.com/blockly/xml">
  <variables>
    <variable id="i">i</variable>
  </variables>
  <block type="controls_for" x="50" y="50">
    <field name="VAR" id="i">i</field>
    <value name="FROM">
      <shadow type="math_number">
        <field name="NUM">1</field>
      </shadow>
    </value>
    <value name="TO">
      <shadow type="math_number">
        <field name="NUM">10</field>
      </shadow>
    </value>
    <value name="BY">
      <shadow type="math_number">
        <field name="NUM">1</field>
      </shadow>
    </value>
    <statement name="DO">
      <block type="text_print">
        <value name="TEXT">
          <block type="variables_get">
            <field name="VAR" id="i">i</field>
          </block>
        </value>
      </block>
    </statement>
  </block>
</xml>`,
};

/** 数学运算示例模板 */
const MATH_OPS_TEMPLATE: TemplateInfo = {
  id: 'math-ops',
  name: '数学运算',
  description: '加减乘除等数学运算示例',
  image: '/img/yiblock/YM-LED.png',
  workspaceXml: `<xml xmlns="https://developers.google.com/blockly/xml">
  <block type="text_print" x="50" y="50">
    <value name="TEXT">
      <block type="math_arithmetic">
        <field name="OP">MULTIPLY</field>
        <value name="A">
          <shadow type="math_number">
            <field name="NUM">6</field>
          </shadow>
        </value>
        <value name="B">
          <shadow type="math_number">
            <field name="NUM">7</field>
          </shadow>
        </value>
      </block>
    </value>
  </block>
</xml>`,
};

/** 文本处理示例模板 */
const TEXT_TOOLS_TEMPLATE: TemplateInfo = {
  id: 'text-tools',
  name: '文本处理',
  description: '文本拼接与长度统计',
  image: '/img/yiblock/YM-AU.png',
  workspaceXml: `<xml xmlns="https://developers.google.com/blockly/xml">
  <block type="text_print" x="50" y="50">
    <value name="TEXT">
      <block type="text_join">
        <mutation items="2"></mutation>
        <value name="ADD0">
          <shadow type="text">
            <field name="TEXT">YiBlock2</field>
          </shadow>
        </value>
        <value name="ADD1">
          <shadow type="text">
            <field name="TEXT">文本</field>
          </shadow>
        </value>
      </block>
    </value>
  </block>
  <block type="text_print" x="50" y="140">
    <value name="TEXT">
      <block type="text_length">
        <value name="VALUE">
          <shadow type="text">
            <field name="TEXT">Hello</field>
          </shadow>
        </value>
      </block>
    </value>
  </block>
</xml>`,
};

/** 变量示例模板 */
const VARIABLES_TEMPLATE: TemplateInfo = {
  id: 'variables-list',
  name: '变量使用',
  description: '创建变量并打印变量值',
  image: '/img/yiblock/YM-SOUND.png',
  workspaceXml: `<xml xmlns="https://developers.google.com/blockly/xml">
  <variables>
    <variable id="score">score</variable>
  </variables>
  <block type="variables_set" x="50" y="50">
    <field name="VAR" id="score">score</field>
    <value name="VALUE">
      <shadow type="math_number">
        <field name="NUM">10</field>
      </shadow>
    </value>
  </block>
  <block type="text_print" x="50" y="130">
    <value name="TEXT">
      <block type="variables_get">
        <field name="VAR" id="score">score</field>
      </block>
    </value>
  </block>
</xml>`,
};

/** 列表操作示例模板 */
const LIST_OPS_TEMPLATE: TemplateInfo = {
  id: 'list-ops',
  name: '列表操作',
  description: '创建列表并获取长度',
  image: '/img/yiblock/YM-RYG.png',
  workspaceXml: `<xml xmlns="https://developers.google.com/blockly/xml">
  <variables>
    <variable id="items">items</variable>
  </variables>
  <block type="variables_set" x="50" y="50">
    <field name="VAR" id="items">items</field>
    <value name="VALUE">
      <block type="lists_create_with">
        <mutation items="3"></mutation>
        <value name="ADD0">
          <shadow type="text">
            <field name="TEXT">A</field>
          </shadow>
        </value>
        <value name="ADD1">
          <shadow type="text">
            <field name="TEXT">B</field>
          </shadow>
        </value>
        <value name="ADD2">
          <shadow type="text">
            <field name="TEXT">C</field>
          </shadow>
        </value>
      </block>
    </value>
  </block>
  <block type="text_print" x="50" y="150">
    <value name="TEXT">
      <block type="lists_length">
        <value name="VALUE">
          <block type="variables_get">
            <field name="VAR" id="items">items</field>
          </block>
        </value>
      </block>
    </value>
  </block>
</xml>`,
};

/** 函数示例模板 */
const FUNCTION_TEMPLATE: TemplateInfo = {
  id: 'function-example',
  name: '函数示例',
  description: '展示如何定义和调用函数',
  image: '/img/yiblock/YM-K2BEEP.png',
  workspaceXml: `<xml xmlns="https://developers.google.com/blockly/xml">
  <block type="procedures_defnoreturn" x="50" y="50">
    <field name="NAME">sayHello</field>
    <statement name="STACK">
      <block type="text_print">
        <value name="TEXT">
          <shadow type="text">
            <field name="TEXT">Hello from function!</field>
          </shadow>
        </value>
      </block>
    </statement>
  </block>
  <block type="procedures_callnoreturn" x="50" y="150">
    <mutation name="sayHello"></mutation>
  </block>
</xml>`,
};

/** IoT WiFi + MQTT 最小示例模板 */
const IOT_WIFI_MQTT_TEMPLATE: TemplateInfo = {
  id: 'iot-wifi-mqtt',
  name: 'IoT WiFi/MQTT',
  description: '连接 WiFi 后初始化 MQTT 并发布一条消息',
  image: '/img/yiblock/YC-AIS.png',
  workspaceXml: `<xml xmlns="https://developers.google.com/blockly/xml">
  <block type="iot_wifi_enable" x="40" y="40">
    <next>
      <block type="iot_wifi_set_ssid_password">
        <value name="SSID">
          <shadow type="text">
            <field name="TEXT">my-ssid</field>
          </shadow>
        </value>
        <value name="PASSWORD">
          <shadow type="text">
            <field name="TEXT">my-password</field>
          </shadow>
        </value>
        <next>
          <block type="iot_wifi_connect">
            <next>
              <block type="controls_if">
                <value name="IF0">
                  <block type="iot_wifi_is_connected"></block>
                </value>
                <statement name="DO0">
                  <block type="iot_mqtt_init">
                    <value name="HOST">
                      <shadow type="text">
                        <field name="TEXT">broker.emqx.io</field>
                      </shadow>
                    </value>
                    <value name="PORT">
                      <shadow type="math_number">
                        <field name="NUM">1883</field>
                      </shadow>
                    </value>
                    <value name="CLIENT_ID">
                      <shadow type="text">
                        <field name="TEXT">yiblock2</field>
                      </shadow>
                    </value>
                    <value name="USERNAME">
                      <shadow type="text">
                        <field name="TEXT">user</field>
                      </shadow>
                    </value>
                    <value name="PASSWORD">
                      <shadow type="text">
                        <field name="TEXT">${MQTT_RUNTIME_PASSWORD_PLACEHOLDER}</field>
                      </shadow>
                    </value>
                    <next>
                      <block type="iot_mqtt_connect">
                        <next>
                          <block type="controls_if">
                            <value name="IF0">
                              <block type="iot_mqtt_is_connected"></block>
                            </value>
                            <statement name="DO0">
                              <block type="iot_mqtt_publish">
                                <value name="TOPIC">
                                  <shadow type="text">
                                    <field name="TEXT">demo/topic</field>
                                  </shadow>
                                </value>
                                <value name="PAYLOAD">
                                  <shadow type="text">
                                    <field name="TEXT">{\"status\":\"ready\"}</field>
                                  </shadow>
                                </value>
                              </block>
                            </statement>
                          </block>
                        </next>
                      </block>
                    </next>
                  </block>
                </statement>
              </block>
            </next>
          </block>
        </next>
      </block>
    </next>
  </block>
</xml>`,
};

/** 所有模板 */
const TEMPLATES: Record<TemplateId, TemplateInfo> = {
  'blank': BLANK_TEMPLATE,
  'hello-world': HELLO_WORLD_TEMPLATE,
  'logic-branch': LOGIC_BRANCH_TEMPLATE,
  'loop-example': LOOP_TEMPLATE,
  'math-ops': MATH_OPS_TEMPLATE,
  'text-tools': TEXT_TOOLS_TEMPLATE,
  'variables-list': VARIABLES_TEMPLATE,
  'list-ops': LIST_OPS_TEMPLATE,
  'function-example': FUNCTION_TEMPLATE,
  'iot-wifi-mqtt': IOT_WIFI_MQTT_TEMPLATE,
};

/**
 * 获取模板信息
 */
export function getTemplate(id: TemplateId): TemplateInfo {
  return TEMPLATES[id] || BLANK_TEMPLATE;
}

/**
 * 获取所有模板列表
 */
export function getAllTemplates(): TemplateInfo[] {
  return Object.values(TEMPLATES);
}

/**
 * 获取示例模板列表（不包含空白模板）
 */
export function getExampleTemplates(): TemplateInfo[] {
  return [
    HELLO_WORLD_TEMPLATE,
    LOGIC_BRANCH_TEMPLATE,
    LOOP_TEMPLATE,
    MATH_OPS_TEMPLATE,
    TEXT_TOOLS_TEMPLATE,
    VARIABLES_TEMPLATE,
    LIST_OPS_TEMPLATE,
    FUNCTION_TEMPLATE,
    IOT_WIFI_MQTT_TEMPLATE,
  ];
}

export default TEMPLATES;
